import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { Tooltip } from "primereact/tooltip";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getErrorTitle } from "../../../constants/constants";
import { ROLE_TYPE_FACILITY_WORKER } from "../../../constants/RegisterConstants";
import { RouterAddress } from "../../../constants/RouterAddress";
import { ReferenceType } from "../../../models/document";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import Unauthorized from "../../../shared/pages/Unauthorized";
import { dashboardPageContainerStyle } from "./dashboardStyles";

const FacilityWorkerDashboard = () => {
  const toast = useRef<Toast>(null);
  const { authStore, backendAPIStore } = useContext(MobXProviderContext);
  const navigate = useNavigate();
  const mobile = useResponsive().isMobile;
  const [facilities, setFacilities] = useState<any[]>([]);
  const [facilitiesLoading, setFacilitiesLoading] = useState(true);
  const [attendanceActionLoading, setAttendanceActionLoading] = useState(false);
  const [attendanceSummary, setAttendanceSummary] = useState<any>(null);
  const [currentShift, setCurrentShift] = useState<any>(null);
  const [binderIncomplete, setBinderIncomplete] = useState<boolean | null>(null);
  const userRoleType = String(authStore.getUserRoleType?.() || "").trim().toLowerCase();

  useEffect(() => {
    void authStore.loadUserContext?.();
  }, [authStore]);

  useEffect(() => {
    const userId = String(authStore.getUserId?.() || "").trim();
    if (!userId) {
      setFacilitiesLoading(false);
      return;
    }

    const loadFacilities = async () => {
      setFacilitiesLoading(true);
      try {
        await backendAPIStore.init();
        const response = await backendAPIStore.getLinkedHomesForCaregiver(userId);
        setFacilities((Array.isArray(response) ? response : [])
          .filter((facility: any) => facility?.active !== false)
          .map((facility: any) => ({
            ...facility,
            homeId: String(facility?.homeId || "").trim(),
            facilityName: String(facility?.businessName || facility?.homeName || "Shared Facility").trim() || "Shared Facility",
          }))
          .filter((facility: any) => facility.homeId));
      } catch {
        setFacilities([]);
      } finally {
        setFacilitiesLoading(false);
      }
    };

    void loadFacilities();
  }, [authStore, backendAPIStore]);

  useEffect(() => {
    const loadStaffFeatures = async () => {
      const userId = String(authStore.getUserId?.() || "").trim();
      if (!userId) return;
      try {
        await backendAPIStore.init();
        const [attendanceResponse, binderResponse] = await Promise.all([
          backendAPIStore.getCaregiverAttendance({ range: "week" }),
          backendAPIStore.getBinderByReference(ReferenceType.USER, userId).catch(() => null),
        ]);
        setAttendanceSummary(attendanceResponse || null);
        setCurrentShift(attendanceResponse?.currentShift || null);
        if (binderResponse) {
          const documents = Array.isArray(binderResponse?.documents) ? binderResponse.documents : [];
          const hasMissingRequiredFolder = (Array.isArray(binderResponse?.folders) ? binderResponse.folders : [])
            .filter((folder: any) => folder?.requiredForCompliance === true)
            .some((folder: any) => !documents.some((document: any) => document?.folderId === folder?.folderId));
          setBinderIncomplete(!binderResponse?.binder || hasMissingRequiredFolder);
        }
      } catch (error: any) {
        toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to load shift details.", life: 3000 });
      }
    };
    void loadStaffFeatures();
  }, [authStore, backendAPIStore]);

  const availableFacilities = useMemo(() => {
    const uniqueFacilities = new Map<string, any>();
    facilities.forEach((facility) => {
      if (!uniqueFacilities.has(facility.homeId)) uniqueFacilities.set(facility.homeId, facility);
    });
    return Array.from(uniqueFacilities.values()).sort((left, right) => left.facilityName.localeCompare(right.facilityName));
  }, [facilities]);

  const refreshAttendance = async () => {
    const response = await backendAPIStore.getCaregiverAttendance({ range: "week" });
    setAttendanceSummary(response || null);
    setCurrentShift(response?.currentShift || null);
  };

  const handleClockIn = async (facility: any) => {
    setAttendanceActionLoading(true);
    try {
      await backendAPIStore.init();
      await backendAPIStore.clockInCaregiverAttendance({
        homeId: facility.homeId,
        homeName: facility.facilityName,
      });
      await refreshAttendance();
      toast.current?.show({ severity: "success", summary: "Clocked In", detail: `Shift started for ${facility.facilityName}.`, life: 3000 });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to clock in.", life: 3000 });
    } finally {
      setAttendanceActionLoading(false);
    }
  };

  const handleClockOut = async () => {
    setAttendanceActionLoading(true);
    try {
      await backendAPIStore.init();
      await backendAPIStore.clockOutCaregiverAttendance();
      await refreshAttendance();
      toast.current?.show({ severity: "success", summary: "Clocked Out", detail: "Shift ended and hours were recorded.", life: 3000 });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to clock out.", life: 3000 });
    } finally {
      setAttendanceActionLoading(false);
    }
  };

  const renderClockButton = (facility: any) => {
    const isCurrentFacilityShift = String(currentShift?.homeId || "").trim() === facility.homeId;
    const employmentProfile = (Array.isArray(attendanceSummary?.employmentProfiles) ? attendanceSummary.employmentProfiles : [])
      .find((profile: any) => String(profile?.homeId || "").trim() === facility.homeId);
    const outsideShift = !isCurrentFacilityShift && employmentProfile?.clockInAllowed !== true;
    const disabled = !isCurrentFacilityShift && (Boolean(currentShift) || outsideShift);
    const disabledMessage = outsideShift
      ? "Clock In is unavailable outside your assigned shift."
      : "Clock out your active facility shift before clocking in here.";

    return <span
      className={disabled ? "facility-worker-clock-action-tooltip" : undefined}
      data-pr-tooltip={disabled ? disabledMessage : undefined}
      data-pr-position="top"
      title={disabled ? disabledMessage : undefined}
      style={{ display: "inline-flex" }}
    >
      <Button
        type="button"
        label={isCurrentFacilityShift ? "Clock Out" : "Clock In"}
        icon={isCurrentFacilityShift ? "pi pi-stop" : "pi pi-play"}
        severity={isCurrentFacilityShift ? "danger" : "success"}
        className="caregiver-clock-action-button"
        rounded
        size="small"
        loading={attendanceActionLoading}
        disabled={disabled}
        style={{
          color: "#ffffff",
          background: isCurrentFacilityShift ? "#dc2626" : "#16a34a",
          borderColor: isCurrentFacilityShift ? "#dc2626" : "#16a34a",
        }}
        onClick={() => isCurrentFacilityShift ? void handleClockOut() : void handleClockIn(facility)}
      />
    </span>;
  };

  const renderFacilityShiftDetails = (facility: any) => {
    const employmentProfile = (Array.isArray(attendanceSummary?.employmentProfiles) ? attendanceSummary.employmentProfiles : [])
      .find((profile: any) => String(profile?.homeId || "").trim() === facility.homeId);
    const accessActive = employmentProfile?.clockInAllowed === true;
    const shiftSummary = String(employmentProfile?.shiftSummary || facility?.shiftSummary || "No shift set").trim() || "No shift set";
    const accessStatus = String(
      employmentProfile?.clockInStatus
      || facility?.accessStatus
      || (accessActive ? "Available Now" : "Outside Shift"),
    ).trim();

    return <div className="flex align-items-center gap-2 flex-wrap mt-2" style={{ color: "#475569", fontSize: "0.85rem" }}>
      <span><strong>Shift:</strong> {shiftSummary}</span>
      <span
        style={{
          border: accessActive ? "1px solid #bbf7d0" : "1px solid #fde68a",
          background: accessActive ? "#f0fdf4" : "#fffbeb",
          color: accessActive ? "#166534" : "#92400e",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "999px",
          padding: "0.15rem 0.55rem",
          fontSize: "0.74rem",
          fontWeight: 700,
        }}
      >
        {accessStatus}
      </span>
    </div>;
  };

  if (userRoleType && userRoleType !== ROLE_TYPE_FACILITY_WORKER) return <Unauthorized />;

  return <div className="container-fluid" style={{ padding: mobile ? "0.75rem" : "1rem" }}>
    <Toast ref={toast} />
    <Tooltip target=".facility-worker-clock-action-tooltip" position="top" showDelay={150} />
    <div style={dashboardPageContainerStyle}>
      <div className="flex align-items-center justify-content-between gap-3 flex-wrap" style={{ marginBottom: "0.85rem" }}>
        <div>
          <div style={{ fontSize: mobile ? "1.25rem" : "1.5rem", fontWeight: 800, color: "#0f172a" }}>Facility Worker Dashboard</div>
          <div style={{ color: "#64748b", marginTop: "0.25rem", fontSize: "0.9rem" }}>Open a shared facility to review your assigned tasks.</div>
        </div>
        <div className="flex align-items-center justify-content-end gap-2 flex-wrap" style={{ marginLeft: mobile ? 0 : "auto", width: mobile ? "100%" : undefined }}>
          <Button
            type="button"
            label="Binder"
            icon="pi pi-book"
            severity="warning"
            rounded
            outlined
            size="small"
            className={binderIncomplete ? "caregiver-binder-incomplete-button" : undefined}
            tooltip={binderIncomplete ? "Complete your Binder" : undefined}
            tooltipOptions={{ position: "top" }}
            style={mobile ? { flex: 1 } : undefined}
            onClick={() => navigate(RouterAddress.getFacilityWorkerJobBinderPagePath())}
          />
          <Button
            type="button"
            label="Shift Details"
            icon="pi pi-clock"
            severity="secondary"
            rounded
            outlined
            size="small"
            style={mobile ? { flex: 1 } : undefined}
            onClick={() => navigate(RouterAddress.getFacilityWorkerAttendancePagePath("today"))}
          />
        </div>
      </div>

      <div className="mb-3">
        <div style={{ fontSize: "0.8rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#64748b" }}>Shared Facilities</div>
      </div>

      {facilitiesLoading ? <div className="flex justify-content-center p-5"><ProgressSpinner role="status" /></div>
        : availableFacilities.length ? <div className="flex flex-column gap-3">
          {availableFacilities.map((facility) => <div key={facility.homeId} style={{ border: "1px solid #e2e8f0", borderRadius: "14px", background: "#ffffff", padding: "0.9rem 1rem" }}>
            <div className="flex align-items-center justify-content-between gap-3 flex-wrap">
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 800, color: "#0f172a", fontSize: "1rem" }}>{facility.facilityName}</div>
                {facility.formattedAddress ? <div style={{ color: "#64748b", marginTop: "0.25rem", fontSize: "0.85rem" }}>{facility.formattedAddress}</div> : null}
                {renderFacilityShiftDetails(facility)}
              </div>
              <div className="flex align-items-center gap-2 flex-wrap">
                {renderClockButton(facility)}
                <Button type="button" label="Facility Tasks" icon="pi pi-check-square" severity="warning" outlined rounded size="small" onClick={() => navigate(RouterAddress.getHomeFacilityTasksPagePath(facility.homeId))} />
              </div>
            </div>
          </div>)}
        </div>
          : <div className="card" style={{ color: "#64748b", lineHeight: 1.6 }}>No shared facilities are assigned to you yet.</div>}
    </div>
  </div>;
};

export default FacilityWorkerDashboard;
